/**
 * 로그인 페이지 (학생용)
 * 게임보이 스타일의 레트로 로그인 화면
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PixelButton } from '../components/common/PixelButton';
import { PixelCard } from '../components/common/PixelCard';
import { PixelInput } from '../components/common/PixelInput';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { soundManager } from '../utils/soundManager';

/**
 * LoginPage 컴포넌트
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoading, setError, error: storeError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  /**
   * 로그인 처리
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    setIsLoading(true);
    setLoading(true);

    try {
      // 1. 로그인 API 호출
      const tokens = await authService.login({ email, password });
      console.log('🔐 로그인 성공, 토큰:', tokens);

      // 2. 토큰을 먼저 저장 (axios 인터셉터가 사용할 수 있도록)
      setAuth({ id: '', email, name: '', role: 'student', group_id: '', is_active: true }, tokens);

      // 3. 사용자 정보 조회 (이제 토큰이 헤더에 포함됨)
      const user = await authService.getCurrentUser();
      console.log('👤 사용자 정보:', user);

      // 4. 학생 역할 확인
      if (user.role !== 'student') {
        setLocalError('학생 계정만 로그인할 수 있습니다');
        setIsLoading(false);
        return;
      }

      // 5. 실제 사용자 정보로 업데이트
      setAuth(user, tokens);

      // 5. 효과음 재생
      soundManager.playCorrect();

      // 6. 홈으로 이동
      navigate('/');
    } catch (error: any) {
      console.error('로그인 실패:', error);

      const errorMessage =
        error.response?.data?.detail || '로그인에 실패했습니다. 다시 시도해주세요.';
      setLocalError(errorMessage);
      setError(errorMessage);

      // 오답 효과음
      soundManager.playWrong();
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 게임보이 본체 */}
        <div
          className="bg-gray-900 p-6 shadow-2xl"
          style={{
            border: '8px solid #374151',
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5), 0 8px 0 #1f2937',
          }}
        >
          {/* LCD 스크린 */}
          <PixelCard variant="screen" padding="large">
            <div className="space-y-6">
              {/* 타이틀 */}
              <div className="text-center mb-8">
                <div
                  className="inline-block bg-[#0f380f] px-6 py-3 mb-4"
                  style={{
                    boxShadow: '4px 4px 0 #306230',
                  }}
                >
                  <h1 className="text-3xl font-pixel text-[#9bbc0f] tracking-wider">
                    채점톡
                  </h1>
                </div>
                <p className="text-[#0f380f] font-pixel text-sm">
                  ▸ 학생 로그인
                </p>
              </div>

              {/* 로그인 폼 */}
              <form onSubmit={handleLogin} className="space-y-4">
                {/* 학생 ID 입력 */}
                <PixelInput
                  label="학생 ID"
                  type="text"
                  placeholder="이메일 입력"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  disabled={isLoading}
                />

                {/* 비밀번호 입력 */}
                <PixelInput
                  label="비밀번호"
                  type="password"
                  placeholder="비밀번호 입력"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  disabled={isLoading}
                  error={localError}
                />

                {/* 로그인 버튼 */}
                <div className="pt-4">
                  <PixelButton
                    variant="screen"
                    size="large"
                    fullWidth
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? '로그인 중...' : '▶ LOGIN'}
                  </PixelButton>
                </div>
              </form>

              {/* 안내 메시지 */}
              <div className="mt-6 text-center">
                <p className="text-[#306230] font-pixel text-xs">
                  강사님께 받은 계정으로 로그인하세요
                </p>
              </div>
            </div>
          </PixelCard>

          {/* 하단 정보 */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-xs font-pixel tracking-widest">
              © 2025 CHAEJUMTALK
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
